// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type RefObject, useEffect, useMemo, useRef, useState, useCallback } from 'react';

import type { UseFormReturnType } from '@mantine/form';

import type { SubmitFormEvent } from 'components/Mantine/Modals/WrappedModal';
import { useGeneralContext } from 'helpers/useGeneralContext';

export type FormType<T> = UseFormReturnType<T> | null;
export type OnSubmitType<T> = ((values: T, event: SubmitFormEvent | undefined) => Promise<void>) | null;
export type ParentFormContextType<T> = {
  form: RefObject<FormType<T>>;
  onSubmit: RefObject<OnSubmitType<T>>;
  setForm: (form: NonNullable<FormType<T>>, onSubmit: NonNullable<OnSubmitType<T>>) => void;
};

// oxlint-disable-next-line typescript/no-explicit-any
export const ParentFormContext = React.createContext<ParentFormContextType<any> | undefined>(undefined);

/**
 * Used to pass the `form` and `onSubmit` props to the parent component
 * when the parent component handles the rendering of the `<form />`
 */
export const useParentForm = <T,>(form: NonNullable<FormType<T>>, onSubmit: NonNullable<OnSubmitType<T>>) => {
  const { setForm, ...context } = useGeneralContext(ParentFormContext, 'ParentForm') as ParentFormContextType<T>;
  useEffect(() => {
    setForm(form, onSubmit);
  }, [setForm, form, onSubmit]);
  return { setForm, ...context };
};

export const useCreateParentForm = <T,>() => {
  const form = useRef<FormType<T>>(null);
  const onSubmit = useRef<OnSubmitType<T>>(null);

  const [isValid, setIsValid] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const setFormWrapped = useCallback<ParentFormContextType<T>['setForm']>((newForm, newOnSubmit) => {
    form.current = newForm;
    onSubmit.current = newOnSubmit;
    setIsValid(newForm.isValid());
    setIsDirty(newForm.isDirty());
  }, []);

  return {
    form,
    onSubmit,
    isValid,
    isDirty,
    setForm: setFormWrapped,
  };
};

/**
 * The shared submission engine behind `FormModal` and `FormPopover`: creates the
 * `ParentFormContext` value the children's `useParentForm` registers into, wraps the
 * registered `onSubmit` with a submitting state, and closes per `closeOnConfirm` once
 * it resolves. Returns the derived props the underlying `WrappedModal` / `WrappedPopover`
 * should receive.
 */
export const useCreateParentFormProvider = <T,>({
  closeOnConfirm = true,
  onClose,
  startDirty,
  loading,
  disabled,
}: {
  /** Whether to close the wrapper after `onSubmit` resolves. @defaultValue true */
  closeOnConfirm?: boolean;
  onClose: () => void;
  /** Enables the submit button even before the form has been modified. */
  startDirty?: boolean;
  /** Additional loading state, used in junction with the internal submitting state. */
  loading?: boolean;
  /** Additional disabled state, used in junction with the form's validity/dirtiness. */
  disabled?: boolean;
}) => {
  const { form, onSubmit, setForm, isValid, isDirty } = useCreateParentForm<T>();

  const contextValue = useMemo(
    () => ({
      setForm,
      form,
      onSubmit,
    }),
    [form, onSubmit, setForm],
  );

  const [submitting, setSubmitting] = useState(false);
  const onSubmitWrapper = useCallback<NonNullable<OnSubmitType<T>>>(
    async (values, event) => {
      setSubmitting(true);
      try {
        await onSubmit.current?.(values, event);
      } finally {
        setSubmitting(false);
      }
      if (closeOnConfirm) onClose();
    },
    [closeOnConfirm, onClose, onSubmit],
  );

  return {
    contextValue,
    isDirty,
    loading: submitting || loading,
    disabled: disabled || !isValid || (!isDirty && !startDirty),
    formSubmit: (e: SubmitFormEvent) => form.current?.onSubmit(onSubmitWrapper)(e),
  };
};
